import React, { useState } from 'react';
import { useApp } from '../App';
import { fsSet, fsLogChange } from '../lib/firebase';
import { syncSignInEmail, listTokensForFamily, revokeToken } from '../lib/tokens';
import CaregiverAccessPanel from './CaregiverAccessPanel';

// ── Edit Caregivers ───────────────────────────────────────────────────────────
// One caregiver directory: family helpers (grandparents), hired sitters, and
// other helpers. Stored in the 'sitters' collection (kept for compatibility);
// caregiverType is written alongside the legacy sitterType field.

const DAY_LABELS = ['S','M','T','W','T','F','S'];

export default function EditCaregivers() {
  const { familyData, setFamilyData, currentUser } = useApp() || {};
  const [sitters, setSitters]   = useState(familyData?.sitters || []);
  const [editingId, setEditingId] = useState(null); // sitter.id | '__new__' | null
  const [form, setForm]           = useState({});
  const [saving, setSaving]       = useState(false);

  const EMPTY = {
    firstName: '', lastName: '', phone: '', email: '', identifier: '',
    sitterType: 'standard', pickupEligible: false, rateOverride: '', blackoutDays: [],
  };

  function startAdd() {
    setEditingId('__new__');
    setForm(EMPTY);
  }

  function startEdit(s) {
    setEditingId(s.id);
    setForm({
      ...EMPTY, ...s,
      rateOverride: s.rateOverride != null ? String(s.rateOverride) : '',
      blackoutDays: Array.isArray(s.blackoutDays) ? s.blackoutDays : [],
    });
  }

  function cancelEdit() { setEditingId(null); setForm({}); }

  function toggleBlackout(idx) {
    setForm(f => ({
      ...f,
      blackoutDays: f.blackoutDays.includes(idx)
        ? f.blackoutDays.filter(d => d !== idx)
        : [...f.blackoutDays, idx],
    }));
  }

  async function saveSitter() {
    if (!form.firstName.trim() || !familyData?.id) return;
    setSaving(true);
    const id = editingId === '__new__' ? `sitter_${Date.now()}` : editingId;
    const original = sitters.find(x => x.id === id);
    const sitter = {
      id,
      firstName:       form.firstName.trim(),
      lastName:        form.lastName.trim(),
      phone:           form.phone.trim(),
      email:           form.email.trim().toLowerCase(),
      identifier:      (form.identifier || form.firstName[0] || 'S').toUpperCase().replace(/[^A-Z0-9\-\/]/g, '').slice(0, 4),
      sitterType:      form.sitterType || 'standard',
      caregiverType:   (form.sitterType || 'standard') === 'family' ? 'family' : 'sitter',
      pickupEligible:  form.pickupEligible || false,
      rateOverride:    form.rateOverride !== '' ? parseFloat(form.rateOverride) : null,
      blackoutDays:    form.blackoutDays || [],
      active:          true,
      access:          original?.access || null,
    };
    await fsSet(`families/${familyData.id}/sitters/${id}`, sitter);
    // Email may have changed while Google sign-in was enabled — keep the
    // family's signInEmails index pointed at the new address.
    if (original?.access?.googleSignInEnabled && original.email !== sitter.email) {
      await syncSignInEmail({
        familyId: familyData.id, email: sitter.email, previousEmail: original.email,
        enabled: true, familyData, setFamilyData,
      });
    }
    await fsLogChange(familyData.id, {
      actorId: familyData.creatorEmail, actorName: familyData.creatorEmail,
      module: 'settings', action: editingId === '__new__' ? 'add_sitter' : 'edit_sitter',
      entityId: id, toValue: sitter.firstName,
    });
    const updated = editingId === '__new__'
      ? [...sitters, sitter]
      : sitters.map(s => s.id === id ? sitter : s);
    setSitters(updated);
    setFamilyData(prev => ({ ...prev, sitters: updated }));
    cancelEdit();
    setSaving(false);
  }

  async function deactivateSitter(s) {
    if (!window.confirm(`Remove ${s.firstName} from active caregivers? This also revokes any access links and sign-in for them.`)) return;
    const updated = { ...s, active: false, access: { ...(s.access || {}), enabled: false, googleSignInEnabled: false } };
    await fsSet(`families/${familyData.id}/sitters/${s.id}`, updated);
    // Cut off any live access — a deactivated caregiver shouldn't keep a
    // working link or Google sign-in.
    const activeTokens = (await listTokensForFamily(familyData.id)).filter(t => t.caregiverId === s.id);
    await Promise.all(activeTokens.map(t => revokeToken(t.token)));
    if (s.access?.googleSignInEnabled) {
      await syncSignInEmail({ familyId: familyData.id, email: s.email, enabled: false, familyData, setFamilyData });
    }
    const list = sitters.map(x => x.id === s.id ? updated : x);
    setSitters(list);
    setFamilyData(prev => ({ ...prev, sitters: list }));
  }

  return (
    <div className="family-edit-panel">
      <div className="edit-list">
        {sitters.filter(s => s.active !== false).map(s => {
          const isEditing = editingId === s.id;
          return (
            <div key={s.id} className={`edit-list-row ${isEditing ? 'edit-list-row--editing' : ''}`}>
              {!isEditing ? (
                <>
                  <div style={{ flex: 1 }}>
                    <div className="edit-list-row__name">
                      <span className="cp-id-chip">({s.identifier || s.firstName[0]})</span>
                      {s.firstName} {s.lastName}
                      <span className={`badge ${s.sitterType === 'family' ? 'badge--green' : 'badge--gray'}`}>
                        {s.sitterType === 'family' ? 'Family' : 'Paid'}
                      </span>
                      {s.pickupEligible && <span className="badge badge--amber">Pickup ✓</span>}
                    </div>
                    {(s.phone || s.email) && (
                      <div className="edit-list-row__sub">{[s.phone, s.email].filter(Boolean).join(' · ')}</div>
                    )}
                  </div>
                  <button className="btn btn--ghost btn--small" onClick={() => startEdit(s)}>Edit</button>
                  <button className="btn btn--ghost btn--small" style={{ color: 'var(--red)' }} onClick={() => deactivateSitter(s)}>Remove</button>
                </>
              ) : (
                <div className="cp-edit-form" style={{ width: '100%' }}>
                  <div className="cp-edit-form__row">
                    <div className="cp-edit-form__field">
                      <label className="cp-edit-form__label">First name</label>
                      <input className="input input--sm" autoFocus value={form.firstName}
                        onChange={e => setForm(f => ({
                          ...f, firstName: e.target.value,
                          identifier: f.identifier || e.target.value[0]?.toUpperCase() || '',
                        }))} />
                    </div>
                    <div className="cp-edit-form__field">
                      <label className="cp-edit-form__label">Last name</label>
                      <input className="input input--sm" value={form.lastName}
                        onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))} />
                    </div>
                    <div className="cp-edit-form__field cp-edit-form__field--id">
                      <label className="cp-edit-form__label">ID</label>
                      <input className="input input--sm input--id" maxLength={4}
                        title="Short ID shown in assignee buttons"
                        value={form.identifier}
                        onChange={e => setForm(f => ({ ...f, identifier: e.target.value.toUpperCase().replace(/[^A-Z0-9\-\/]/g, '') }))} />
                    </div>
                  </div>
                  <div className="cp-edit-form__row">
                    <div className="cp-edit-form__field">
                      <label className="cp-edit-form__label">Phone <span className="cp-edit-form__hint">(optional)</span></label>
                      <input className="input input--sm" value={form.phone}
                        onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
                    </div>
                    <div className="cp-edit-form__field">
                      <label className="cp-edit-form__label">Email <span className="cp-edit-form__hint">(optional)</span></label>
                      <input className="input input--sm" value={form.email}
                        onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
                    </div>
                  </div>
                  <div className="sitter-type-row">
                    <label className="sitter-type-label">Type</label>
                    <div className="sitter-type-options">
                      {[
                        { id: 'standard', label: 'Paid', desc: 'Paid — hourly rate' },
                        { id: 'family',   label: 'Family',   desc: '$0 default — grandparents, relatives' },
                      ].map(t => (
                        <button key={t.id} type="button"
                          className={`sitter-type-btn ${form.sitterType === t.id ? 'active' : ''}`}
                          onClick={() => setForm(f => ({ ...f, sitterType: t.id, rateOverride: t.id === 'family' ? '0' : f.rateOverride }))}>
                          <span className="sitter-type-btn__label">{t.label}</span>
                          <span className="sitter-type-btn__desc">{t.desc}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="cp-edit-form__row" style={{ alignItems: 'center' }}>
                    <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                      <input type="checkbox" checked={form.pickupEligible}
                        onChange={e => setForm(f => ({ ...f, pickupEligible: e.target.checked }))} />
                      Pickup eligible
                    </label>
                    <div className="cp-edit-form__field cp-edit-form__field--narrow">
                      <label className="cp-edit-form__label">Rate override <span className="cp-edit-form__hint">(optional — $/hr)</span></label>
                      <input className="input input--sm input--short" value={form.rateOverride}
                        onChange={e => setForm(f => ({ ...f, rateOverride: e.target.value }))} />
                    </div>
                  </div>
                  <div className="cp-edit-form__field">
                    <label className="cp-edit-form__label">Weekly blackout days</label>
                    <div className="blackout-days__toggles">
                      {DAY_LABELS.map((d, i) => (
                        <button key={i} type="button"
                          className={`day-toggle ${form.blackoutDays.includes(i) ? 'active' : ''}`}
                          onClick={() => toggleBlackout(i)}>{d}</button>
                      ))}
                    </div>
                  </div>
                  <div className="cp-edit-form__actions">
                    <button className="btn btn--primary btn--small" onClick={saveSitter} disabled={saving || !form.firstName.trim()}>
                      {saving ? 'Saving…' : 'Save'}
                    </button>
                    <button className="btn btn--ghost btn--small" onClick={cancelEdit}>Cancel</button>
                  </div>
                  <CaregiverAccessPanel
                    familyData={familyData}
                    setFamilyData={setFamilyData}
                    currentUser={currentUser}
                    sitter={s}
                    onAccessChange={updated => setSitters(prev => prev.map(x => x.id === updated.id ? updated : x))}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {editingId === '__new__' ? (
        <div className="cp-edit-form" style={{ marginTop: 12 }}>
          <div className="child-form-section" style={{ marginBottom: 8 }}>New Caregiver</div>
          <div className="cp-edit-form__row">
            <div className="cp-edit-form__field">
              <label className="cp-edit-form__label">First name</label>
              <input className="input input--sm" autoFocus value={form.firstName}
                onChange={e => setForm(f => ({
                  ...f, firstName: e.target.value,
                  identifier: f.identifier || e.target.value[0]?.toUpperCase() || '',
                }))} />
            </div>
            <div className="cp-edit-form__field">
              <label className="cp-edit-form__label">Last name</label>
              <input className="input input--sm" value={form.lastName}
                onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))} />
            </div>
            <div className="cp-edit-form__field cp-edit-form__field--id">
              <label className="cp-edit-form__label">ID</label>
              <input className="input input--sm input--id" maxLength={4}
                value={form.identifier}
                onChange={e => setForm(f => ({ ...f, identifier: e.target.value.toUpperCase().replace(/[^A-Z0-9\-\/]/g, '') }))} />
            </div>
          </div>
          <div className="cp-edit-form__row">
            <div className="cp-edit-form__field">
              <label className="cp-edit-form__label">Phone <span className="cp-edit-form__hint">(optional)</span></label>
              <input className="input input--sm" value={form.phone}
                onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
            </div>
            <div className="cp-edit-form__field">
              <label className="cp-edit-form__label">Email <span className="cp-edit-form__hint">(optional)</span></label>
              <input className="input input--sm" value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
            </div>
          </div>
          <div className="sitter-type-row">
            <label className="sitter-type-label">Type</label>
            <div className="sitter-type-options">
              {[
                { id: 'standard', label: 'Sitter', desc: 'Paid — hourly rate' },
                { id: 'family',   label: 'Family',   desc: '$0 default — grandparents, relatives' },
              ].map(t => (
                <button key={t.id} type="button"
                  className={`sitter-type-btn ${form.sitterType === t.id ? 'active' : ''}`}
                  onClick={() => setForm(f => ({ ...f, sitterType: t.id, rateOverride: t.id === 'family' ? '0' : f.rateOverride }))}>
                  <span className="sitter-type-btn__label">{t.label}</span>
                  <span className="sitter-type-btn__desc">{t.desc}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="cp-edit-form__row" style={{ alignItems: 'center' }}>
            <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.pickupEligible}
                onChange={e => setForm(f => ({ ...f, pickupEligible: e.target.checked }))} />
              Pickup eligible
            </label>
            <div className="cp-edit-form__field cp-edit-form__field--narrow">
              <label className="cp-edit-form__label">Rate override <span className="cp-edit-form__hint">(optional — $/hr)</span></label>
              <input className="input input--sm input--short" value={form.rateOverride}
                onChange={e => setForm(f => ({ ...f, rateOverride: e.target.value }))} />
            </div>
          </div>
          <div className="cp-edit-form__field">
            <label className="cp-edit-form__label">Weekly blackout days</label>
            <div className="blackout-days__toggles">
              {DAY_LABELS.map((d, i) => (
                <button key={i} type="button"
                  className={`day-toggle ${form.blackoutDays.includes(i) ? 'active' : ''}`}
                  onClick={() => toggleBlackout(i)}>{d}</button>
              ))}
            </div>
          </div>
          <div className="cp-edit-form__actions">
            <button className="btn btn--primary btn--small" onClick={saveSitter} disabled={saving || !form.firstName.trim()}>
              {saving ? 'Saving…' : '+ Add Caregiver'}
            </button>
            <button className="btn btn--ghost btn--small" onClick={cancelEdit}>Cancel</button>
          </div>
        </div>
      ) : (
        <button className="btn btn--secondary" style={{ marginTop: 8 }} onClick={startAdd}>+ Add Caregiver</button>
      )}
    </div>
  );
}


